import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, updateProfile, isLoading, error } = useAuthStore();
  
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    location: '',
    avatar: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        bio: user.bio || '',
        location: user.location || '',
        avatar: user.avatar || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await updateProfile(formData);
    if (result.success) {
      navigate('/profile');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real app, you would upload this to your server
      // For now, we'll just set a placeholder URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, avatar: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl">
          <div className="px-6 py-8">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Edit Profile</h1>
              <button
                onClick={() => navigate('/profile')}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Cancel
              </button>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-1/3">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Profile Picture
                  </label>
                  <div className="relative">
                    <img
                      src={formData.avatar}
                      alt="Profile"
                      className="w-32 h-32 rounded-full object-cover border-2 border-neutral-200 dark:border-neutral-700"
                    />
                    <label className="absolute bottom-0 right-0 bg-primary-500 hover:bg-primary-600 text-white p-2 rounded-full cursor-pointer shadow-sm">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="md:w-2/3 space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Bio
                    </label>
                    <textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows="4"
                      className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Tell us about yourself..."
                    />
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;

// import React, { useState } from 'react';
// import DashboardLayout from '../components/DashboardLayout';

// const EditProfile = () => {
//   const [formData, setFormData] = useState({
//     name: 'Emma Johnson',
//     email: 'emma.johnson@example.com',
//     bio: 'Passionate frontend developer with a focus on React and modern JavaScript. Currently expanding my skills in full-stack development.',
//     location: 'San Francisco, CA',
//     currentPassword: '',
//     newPassword: '',
//     confirmPassword: '',
//     avatar: null,
//     notifications: {
//       email: true,
//       push: true,
//       courseUpdates: true,
//       messages: true,
//       marketing: false,
//     },
//   });

//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [activeTab, setActiveTab] = useState('profile');

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
    
//     if (type === 'checkbox') {
//       if (name.startsWith('notifications.')) {
//         const notificationKey = name.split('.')[1];
//         setFormData({
//           ...formData,
//           notifications: {
//             ...formData.notifications,
//             [notificationKey]: checked,
//           },
//         });
//       } else {
//         setFormData({ ...formData, [name]: checked });
//       }
//     } else {
//       setFormData({ ...formData, [name]: value });
//     }
//   };

//   const handleFileChange = (e) => {
//     setFormData({ ...formData, avatar: e.target.files[0] });
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
    
//     // Validate password change
//     if (formData.newPassword || formData.confirmPassword) {
//       if (formData.newPassword !== formData.confirmPassword) {
//         alert('New passwords do not match');
//         setIsSubmitting(false);
//         return;
//       }
//       if (formData.newPassword.length < 6) {
//         alert('Password must be at least 6 characters');
//         setIsSubmitting(false);
//         return;
//       }
//     }
    
//     // Simulate API call
//     setTimeout(() => {
//       setIsSubmitting(false);
//       alert('Profile updated successfully!');
//     }, 1500);
//   };

//   return (
//     <DashboardLayout userRole="student">
//       <div className="max-w-4xl mx-auto">
//         <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">Edit Profile & Settings</h1>
        
//         {/* Tabs */}
//         <div className="mb-8">
//           <div className="border-b border-neutral-200 dark:border-neutral-700">
//             <nav className="-mb-px flex space-x-8">
//               {['profile', 'password', 'notifications'].map(tab => (
//                 <button
//                   key={tab}
//                   onClick={() => setActiveTab(tab)}
//                   className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
//                     activeTab === tab
//                       ? 'border-primary-500 text-primary-600 dark:text-primary-400'
//                       : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
//                   }`}
//                 >
//                   {tab}
//                 </button>
//               ))}
//             </nav>
//           </div>
//         </div>

//         <form onSubmit={handleSubmit} className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
//           {activeTab === 'profile' && (
//             <>
//               <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">Profile Information</h2>
              
//               {/* Avatar Upload */}
//               <div className="mb-6">
//                 <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Profile Picture
//                 </label>
//                 <div className="flex items-center space-x-6">
//                   <div>
//                     <img
//                       src={formData.avatar ? URL.createObjectURL(formData.avatar) : 'https://placehold.co/100x100/0ea5e9/ffffff?text=EJ'}
//                       alt="Profile"
//                       className="w-24 h-24 rounded-full border-2 border-white dark:border-neutral-800 shadow-md"
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-2">
//                       Upload a new profile picture
//                     </label>
//                     <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-lg hover:border-neutral-400 dark:hover:border-neutral-500">
//                       <div className="space-y-1 text-center">
//                         <svg className="mx-auto h-8 w-8 text-neutral-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
//                           <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         </svg>
//                         <div className="flex text-sm text-neutral-600 dark:text-neutral-400">
//                           <label className="relative cursor-pointer bg-white dark:bg-neutral-800 rounded-md font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
//                             <span>Upload a file</span>
//                             <input
//                               type="file"
//                               onChange={handleFileChange}
//                               className="sr-only"
//                               accept="image/*"
//                             />
//                           </label>
//                         </div>
//                         <p className="text-xs text-neutral-500 dark:text-neutral-500">PNG, JPG, GIF up to 10MB</p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Name */}
//               <div className="mb-6">
//                 <label htmlFor="name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Full Name
//                 </label>
//                 <input
//                   id="name"
//                   name="name"
//                   type="text"
//                   value={formData.name}
//                   onChange={handleChange}
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="Your full name"
//                 />
//               </div>

//               {/* Email */}
//               <div className="mb-6">
//                 <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Email Address
//                 </label>
//                 <input
//                   id="email"
//                   name="email"
//                   type="email"
//                   value={formData.email}
//                   onChange={handleChange}
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="you@example.com"
//                 />
//               </div>

//               {/* Bio */}
//               <div className="mb-6">
//                 <label htmlFor="bio" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Bio
//                 </label>
//                 <textarea
//                   id="bio"
//                   name="bio"
//                   value={formData.bio}
//                   onChange={handleChange}
//                   rows="4"
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="Tell us about yourself..."
//                 ></textarea>
//                 <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-500">Max 500 characters</p>
//               </div>

//               {/* Location */}
//               <div className="mb-6">
//                 <label htmlFor="location" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Location
//                 </label>
//                 <input
//                   id="location"
//                   name="location"
//                   type="text"
//                   value={formData.location}
//                   onChange={handleChange}
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="City, State, Country"
//                 />
//               </div>
//             </>
//           )}

//           {activeTab === 'password' && (
//             <>
//               <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">Change Password</h2>
              
//               <div className="mb-6">
//                 <label htmlFor="currentPassword" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Current Password
//                 </label>
//                 <input
//                   id="currentPassword"
//                   name="currentPassword"
//                   type="password"
//                   value={formData.currentPassword}
//                   onChange={handleChange}
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="Enter your current password"
//                 />
//               </div>

//               <div className="mb-6">
//                 <label htmlFor="newPassword" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   New Password
//                 </label>
//                 <input
//                   id="newPassword"
//                   name="newPassword"
//                   type="password"
//                   value={formData.newPassword}
//                   onChange={handleChange}
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="Enter your new password"
//                 />
//                 <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-500">Password must be at least 6 characters</p>
//               </div>

//               <div className="mb-6">
//                 <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
//                   Confirm New Password
//                 </label>
//                 <input
//                   id="confirmPassword"
//                   name="confirmPassword"
//                   type="password"
//                   value={formData.confirmPassword}
//                   onChange={handleChange}
//                   className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
//                   placeholder="Confirm your new password"
//                 />
//               </div>
//             </>
//           )}

//           {activeTab === 'notifications' && (
//             <>
//               <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">Notification Preferences</h2>
              
//               <div className="space-y-4">
//                 <div className="flex items-center justify-between py-3 border-b border-neutral-200 dark:border-neutral-700">
//                   <div>
//                     <p className="font-medium text-neutral-900 dark:text-white">Email Notifications</p>
//                     <p className="text-sm text-neutral-600 dark:text-neutral-400">Receive notifications via email</p>
//                   </div>
//                   <input
//                     type="checkbox"
//                     name="notifications.email"
//                     checked={formData.notifications.email}
//                     onChange={handleChange}
//                     className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
//                   />
//                 </div>

//                 <div className="flex items-center justify-between py-3 border-b border-neutral-200 dark:border-neutral-700">
//                   <div>
//                     <p className="font-medium text-neutral-900 dark:text-white">Push Notifications</p>
//                     <p className="text-sm text-neutral-600 dark:text-neutral-400">Receive browser push notifications</p>
//                   </div>
//                   <input
//                     type="checkbox"
//                     name="notifications.push"
//                     checked={formData.notifications.push}
//                     onChange={handleChange}
//                     className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
//                   />
//                 </div>

//                 <div className="flex items-center justify-between py-3 border-b border-neutral-200 dark:border-neutral-700">
//                   <div>
//                     <p className="font-medium text-neutral-900 dark:text-white">Course Updates</p>
//                     <p className="text-sm text-neutral-600 dark:text-neutral-400">Get notified about course updates and announcements</p>
//                   </div>
//                   <input
//                     type="checkbox"
//                     name="notifications.courseUpdates"
//                     checked={formData.notifications.courseUpdates}
//                     onChange={handleChange}
//                     className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
//                   />
//                 </div>

//                 <div className="flex items-center justify-between py-3 border-b border-neutral-200 dark:border-neutral-700">
//                   <div>
//                     <p className="font-medium text-neutral-900 dark:text-white">Messages</p>
//                     <p className="text-sm text-neutral-600 dark:text-neutral-400">Get notified when you receive new messages</p>
//                   </div>
//                   <input
//                     type="checkbox"
//                     name="notifications.messages"
//                     checked={formData.notifications.messages}
//                     onChange={handleChange}
//                     className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
//                   />
//                 </div>

//                 <div className="flex items-center justify-between py-3">
//                   <div>
//                     <p className="font-medium text-neutral-900 dark:text-white">Marketing Emails</p>
//                     <p className="text-sm text-neutral-600 dark:text-neutral-400">Receive promotional emails and special offers</p>
//                   </div>
//                   <input
//                     type="checkbox"
//                     name="notifications.marketing"
//                     checked={formData.notifications.marketing}
//                     onChange={handleChange}
//                     className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
//                   />
//                 </div>
//               </div>
//             </>
//           )}

//           <div className="flex justify-end pt-6 border-t border-neutral-200 dark:border-neutral-700">
//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
//             >
//               {isSubmitting ? (
//                 <span className="flex items-center">
//                   <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
//                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                   </svg>
//                   Saving...
//                 </span>
//               ) : (
//                 'Save Changes'
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </DashboardLayout>
//   );
// };

// export default EditProfile;